import { useEffect, useRef, useState } from 'react';
import { MfeOutlet } from './MfeOutlet';
import { usePopupMfeStore, closePopupMfe } from './popup-store';
import { popupMfeHeaderText, popupMfePanelClassName } from './popup-mfe-constants';
import { ensurePopupAccess } from './popup-access';
import { useClientStore } from '@/client/client-store';
import { eventsModule } from '@/events/events-module';
import { getTracker } from '@/telemetry/tracker';
import { displayLoader, hideLoader } from '@/ui/loader-store';
import { pushToast } from '@/ui/toast-store';

const POPUP_ACCESS_DENIED_TOAST = "You don't have access to the Header App.";

/**
 * Renders the active popup MFE in an overlay above the routed content.
 *
 * Mirrors Angular's `modal-popup` component: no shell chrome (no header, no
 * close button, no width constraint). The MFE owns its own modal dialog,
 * backdrop, sizing, and close button; the shell provides only a full-viewport
 * mount point.
 */
export function PopupMfeOverlay() {
  const appId = usePopupMfeStore((s) => s.appId);
  const subPath = usePopupMfeStore((s) => s.subPath);
  const currentClient = useClientStore((s) => s.current);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [allowed, setAllowed] = useState(false);

  // Access gate — parity with the legacy `BaseRouteGuard` popup validation. A
  // popup opened by direct URL (`?popup=…`) or an imperative call must pass the
  // current client's role and tile gate before it mounts. On denial the overlay
  // shows a toast and closes, which strips the `?popup=` param via
  // `usePopupUrlSync` — mirroring the legacy "navigate back to the primary URL"
  // behaviour. Header-initiated opens already satisfy this gate, so they pass
  // with no user-visible change.
  useEffect(() => {
    setAllowed(false);
    if (!appId || !currentClient) return;
    let cancelled = false;
    void ensurePopupAccess(appId, currentClient.id).then((ok) => {
      if (cancelled) return;
      if (ok) {
        setAllowed(true);
        return;
      }
      getTracker().event('Popup-mfe-access-denied', { appId, clientId: currentClient.id });
      pushToast({ variant: 'error', message: POPUP_ACCESS_DENIED_TOAST });
      closePopupMfe();
    });
    return () => {
      cancelled = true;
    };
  }, [appId, currentClient?.id]);

  useEffect(() => {
    if (!appId || !allowed) return;
    const close = () => {

      eventsModule.emit('shell:popup-mfe-closed', { appId });
      closePopupMfe();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [appId, allowed]);

  // Block the whole shell (home, side nav, header) behind the full-viewport blur
  // loader while the popup MFE bundle downloads and bootstraps; `MfeOutlet` fires
  // `onLoaded` (on both success and error) to hide it. Shown only after access is
  // granted, so a denied popup never displays the loader.
  // Include `subPath` in deps so navigating within the same popup (e.g. STF
  // contact-support → create-ticket) also shows the loader during remount.
  useEffect(() => {
    if (!appId || !allowed) return;
    displayLoader();
    return () => hideLoader();
  }, [appId, allowed, subPath]);

  if (!appId || !allowed) return null;

  return (
    <div
      ref={panelRef}
      className={popupMfePanelClassName(appId)}
      role="dialog"
      aria-modal="false"
      aria-label={popupMfeHeaderText(appId)}
    >
      {currentClient ? (
        <MfeOutlet
          key={`popup:${appId}:${currentClient.id}:${subPath ?? ''}`}
          appId={appId}
          layout="fill-parent"
          suppressSpinner
          onLoaded={hideLoader}
          mfeProps={{
            clientId: currentClient.id,
            client: {
              clientId: currentClient.id,
              clientName: currentClient.name,
              isFavorite: currentClient.isFavorite,
            },
            baseHref: `/app/${appId}`,
            routeParams: [],
            context: 'popup',
            route: {
              pathname: subPath ? `/${appId}/${subPath}` : `/${appId}`,
              params: {},
              search: typeof window !== 'undefined' ? window.location.search : '',
            },
          }}
        />
      ) : null}
    </div>
  );
}
